import "../party/partyShell.css";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { PartyShellModeCardPreview } from "../party/PartyShellModeCardPreview";
import { usePartyShellGames } from "../party/usePartyShellGames";
import type { PartyShellPalette } from "../theme/theme";
import { MixpanelEvents, trackEvent } from "../utils/mixpanel";
import { markOnboardingCompleted } from "../utils/onboardingCompleted";

interface OnboardingScreenProps {
  onComplete: () => void;
}

/**
 * Onboarding pages in order. Add or remove ids here and mirror keys under
 * `onboarding.flow.<id>` in locale files.
 */
const FLOW_STEP_IDS = ["welcome", "browseModes", "hostGame", "ready"] as const;

const OnboardingScreen: React.FC<OnboardingScreenProps> = ({ onComplete }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const p = (theme.palette as unknown as { party: PartyShellPalette }).party;
  const { games, isLoading } = usePartyShellGames();

  const [currentStep, setCurrentStep] = useState(0);

  const isRTL = i18n.language === "ar";

  const steps = useMemo(
    () =>
      FLOW_STEP_IDS.map((id) => ({
        id,
        title: t(`onboarding.flow.${id}.title`),
        description: t(`onboarding.flow.${id}.body`),
      })),
    [t]
  );

  const current = steps[currentStep];
  const currentId = FLOW_STEP_IDS[currentStep];

  const hostIcons = useMemo(
    () => [<TouchAppOutlinedIcon key="pick" />, <TuneOutlinedIcon key="setup" />, <PlayArrowIcon key="start" />],
    []
  );

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    trackEvent(MixpanelEvents.ONBOARDING_COMPLETED, {
      current_step: currentStep,
      total_steps: steps.length,
    });
    markOnboardingCompleted();
    onComplete();
  };

  const handleSkip = () => {
    trackEvent(MixpanelEvents.ONBOARDING_SKIPPED, {
      current_step: currentStep,
      total_steps: steps.length,
    });
    handleComplete();
  };

  return (
    <Box
      className="party-shell-root"
      data-party-theme="midnight"
      data-party-type="rubik"
      dir={isRTL ? "rtl" : "ltr"}
      lang={i18n.language || "en"}
      sx={{
        position: "fixed",
        inset: 0,
        maxHeight: "100dvh",
        height: "100dvh",
        zIndex: 9999,
        color: "var(--ink)",
        fontFamily: "var(--font-body)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        pb: "var(--safe-bot)",
        pt: "var(--safe-top)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "60px",
          insetInlineEnd: { xs: 12, sm: 20 },
          zIndex: 10,
        }}
      >
        <Button
          type="button"
          onClick={handleSkip}
          className="party-mono"
          sx={{
            color: p.inkDim,
            fontWeight: 700,
            textTransform: "none",
            fontSize: { xs: "0.75rem", sm: "0.85rem" },
            letterSpacing: "0.08em",
            borderRadius: "var(--r-pill)",
            px: 1.5,
            py: 0.5,
            minHeight: 36,
            border: `1px solid ${p.inkFaint}`,
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.06)",
              borderColor: p.inkDim,
            },
          }}
        >
          {t("onboarding.skip")}
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          px: { xs: 2, sm: 3 },
          pt: { xs: 3.5, sm: 4.5 },
          pb: { xs: 1, sm: 1.5 },
          position: "relative",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            // backgroundImage: `radial-gradient(min(100vw, 520px) min(45vh, 380px) at 80% -8%, color-mix(in oklab, var(--hot) 16%, transparent), transparent 55%),
            //   radial-gradient(min(90vw, 480px) min(40vh, 340px) at -5% 102%, color-mix(in oklab, var(--violet) 18%, transparent), transparent 52%)`,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: 520,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
            overflowX: "hidden",
          }}
        >
          <Stack spacing={1} sx={{ flexShrink: 0, alignItems: "center", textAlign: "center", mb: { xs: 0.5, sm: 0.75 } }}>
            <Chip
              size="small"
              label={t("onboarding.kicker")}
              className="party-mono"
              sx={{
                height: 26,
                fontWeight: 800,
                letterSpacing: "0.06em",
                fontSize: 10,
                color: p.ink,
                // bgcolor: "rgba(255,255,255,0.08)",
                // border: `1px solid ${p.inkFaint}`,
              }}
            />
            <Typography
              component="p"
              className="party-mono"
              sx={{ color: p.inkDim, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em" }}
            >
              {t("onboarding.progress", { current: currentStep + 1, total: steps.length })}
            </Typography>
          </Stack>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "stretch",
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
              py: { xs: 0.5, sm: 1 },
              minWidth: 0,
            }}
          >
            <Stack
              spacing={{ xs: 2, sm: 2.25 }}
              sx={{
                width: "100%",
                alignItems: "center",
                flexShrink: 0,
                minWidth: 0,
                overflowX: "hidden",
              }}
            >
              {currentId === "browseModes" && (
                <Box
                  sx={{
                    width: "100dvw",
                    maxWidth: "100vw",
                    position: "relative",
                    left: "50%",
                    right: "auto",
                    transform: "translateX(-50%)",
                    flexShrink: 0,
                    py: 0.5,
                    overflowX: "hidden",
                  }}
                >
                  <Box
                    role="list"
                    aria-label={t("onboarding.modesRailAria")}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "nowrap",
                      gap: { xs: 1.25, sm: 1.5, md: 1.75 },
                      overflowX: "auto",
                      overflowY: "hidden",
                      WebkitOverflowScrolling: "touch",
                      scrollSnapType: "x proximity",
                      scrollPaddingInline: { xs: 20, sm: 24, md: 32 },
                      paddingInlineStart: { xs: "max(20px, env(safe-area-inset-left, 0px))", sm: 24, md: 32 },
                      paddingInlineEnd: {
                        xs: "max(20px, calc(50dvw - 72px), env(safe-area-inset-right, 0px))",
                        sm: "max(24px, calc(50dvw - 96px), env(safe-area-inset-right, 0px))",
                        md: "max(28px, calc(50dvw - 120px), env(safe-area-inset-right, 0px))",
                        lg: "max(32px, calc(50dvw - 160px), env(safe-area-inset-right, 0px))",
                      },
                      pb: 0.75,
                      scrollbarWidth: "thin",
                      "&::-webkit-scrollbar": { height: 6 },
                      "&::-webkit-scrollbar-thumb": {
                        borderRadius: 999,
                        bgcolor: "rgba(255,255,255,0.18)",
                      },
                    }}
                  >
                    {isLoading && games.length === 0 ? (
                      <Fragment>
                        {[0, 1, 2, 3].map((k) => (
                          <Skeleton
                            key={k}
                            variant="rounded"
                            role="listitem"
                            sx={{
                              flex: "0 0 auto",
                              width: {
                                xs: "min(88vw, 200px)",
                                sm: "min(72vw, 280px)",
                                md: "min(62vw, 360px)",
                                lg: "min(56vw, 480px)",
                                xl: "min(52vw, 560px)",
                              },
                              minWidth: { xs: 148, sm: 220, md: 280, lg: 340, xl: 400 },
                              height: 168,
                              scrollSnapAlign: "center",
                              borderRadius: "var(--r-card)",
                              bgcolor: "rgba(255,255,255,0.06)",
                            }}
                          />
                        ))}
                      </Fragment>
                    ) : (
                      games.map((g, i) => (
                        <Box
                          key={g.id}
                          role="listitem"
                          sx={{
                            flex: "0 0 auto",
                            width: {
                              xs: "min(88vw, 200px)",
                              sm: "min(72vw, 280px)",
                              md: "min(62vw, 360px)",
                              lg: "min(56vw, 480px)",
                              xl: "min(52vw, 560px)",
                            },
                            minWidth: { xs: 148, sm: 220, md: 280, lg: 340, xl: 400 },
                            scrollSnapAlign: "center",
                          }}
                        >
                          <PartyShellModeCardPreview game={g} index={i + 1} compact />
                        </Box>
                      ))
                    )}
                    <Box
                      aria-hidden
                      sx={{
                        flex: "0 0 auto",
                        width: {
                          xs: "max(48px, min(200px, calc(50dvw - 80px)))",
                          sm: "max(56px, min(240px, calc(50dvw - 100px)))",
                          md: "max(64px, min(280px, calc(50dvw - 140px)))",
                        },
                        minWidth: { xs: 40, sm: 48 },
                        alignSelf: "stretch",
                      }}
                    />
                  </Box>
                </Box>
              )}

              {currentId === "welcome" && (
                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                  <Box
                    component="img"
                    src="/assets/kan.png"
                    alt={t("common.appName")}
                    sx={{
                      height: { xs: 112, sm: 132, md: 152 },
                      width: "auto",
                      maxWidth: "min(78vw, 320px)",
                      objectFit: "contain",
                      display: "block",
                      filter: `drop-shadow(0 12px 32px color-mix(in oklab, ${p.hot} 28%, transparent))`,
                    }}
                  />
                </Box>
              )}

              {currentId === "hostGame" && (
                <Stack spacing={1.25} sx={{ width: "100%" }}>
                  {(["pick", "setup", "start"] as const).map((stepKey, idx) => (
                    <Stack
                      key={stepKey}
                      direction="row"
                      spacing={1.25}
                      alignItems="flex-start"
                      sx={{
                        p: { xs: 1.25, sm: 1.5 },
                        borderRadius: "var(--r-card)",
                        border: `1px solid ${p.inkFaint}`,
                        bgcolor: "rgba(255,255,255,0.04)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(255,255,255,0.08)",
                          color: p.violet,
                          "& svg": { fontSize: 22 },
                        }}
                      >
                        {hostIcons[idx]}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: "0.92rem", sm: "1rem" }, lineHeight: 1.25 }}>
                          {t(`onboarding.hostSteps.${stepKey}.title`)}
                        </Typography>
                        <Typography sx={{ mt: 0.35, fontSize: { xs: "0.82rem", sm: "0.9rem" }, color: p.inkDim, lineHeight: 1.45 }}>
                          {t(`onboarding.hostSteps.${stepKey}.body`)}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              )}

              {currentId === "ready" && (
                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                  <Box
                    sx={{
                      width: { xs: 96, sm: 112 },
                      height: { xs: 96, sm: 112 },
                      borderRadius: "50%",
                      border: `2px solid color-mix(in oklab, ${p.gold} 40%, transparent)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(255,255,255,0.05)",
                    }}
                  >
                    <CheckCircleOutlineIcon sx={{ fontSize: { xs: 48, sm: 56 }, color: p.gold }} />
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  width: "100%",
                  maxWidth: 480,
                  mx: "auto",
                  overflowX: "hidden",
                  boxSizing: "border-box",
                }}
              >
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 900,
                    letterSpacing: "-0.02em",
                    fontSize: { xs: "1.45rem", sm: "1.75rem", md: "1.95rem" },
                    lineHeight: 1.32,
                    textAlign: "center",
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "visible",
                    pt: "0.2em",
                    pb: "0.28em",
                    px: 0.5,
                    mb: { xs: 1, sm: 1.25 },
                    background: `linear-gradient(115deg, ${p.ink} 0%, color-mix(in oklab, ${p.ink} 72%, ${p.hot}) 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    /** `background-clip: text` paints the glyph box; extra padding avoids clipping descenders */
                  }}
                >
                  {current.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    color: p.inkDim,
                    fontWeight: 600,
                    lineHeight: 1.55,
                    textAlign: "center",
                    maxWidth: "100%",
                    mx: "auto",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  {current.description}
                </Typography>
                {currentId === "browseModes" && (
                  <Typography
                    className="party-mono"
                    sx={{
                      mt: 1.25,
                      textAlign: "center",
                      fontSize: 10,
                      letterSpacing: "0.05em",
                      color: p.inkDim,
                      maxWidth: "100%",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                    }}
                  >
                    {t("onboarding.modesCaption")}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          pb: { xs: 1.5, sm: 2 },
          pt: { xs: 1, sm: 1.25 },
          px: { xs: 1.5, sm: 2 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: isRTL ? "row-reverse" : "row",
          width: "100%",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: { xs: 44, sm: 52 }, display: "flex", justifyContent: "flex-start" }}>
          {currentStep > 0 ? (
            <IconButton
              type="button"
              onClick={handleBack}
              aria-label={t("common.back")}
              sx={{
                width: { xs: 44, sm: 52 },
                height: { xs: 44, sm: 52 },
                borderRadius: "50%",
                bgcolor: "rgba(255, 255, 255, 0.08)",
                color: p.ink,
                border: `1.5px solid ${p.inkFaint}`,
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.14)",
                  transform: "scale(1.04)",
                },
                transition: "transform 0.2s ease, background-color 0.2s ease",
              }}
            >
              <ArrowBackIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />
            </IconButton>
          ) : (
            <Box sx={{ width: { xs: 44, sm: 52 } }} aria-hidden />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexDirection: isRTL ? "row-reverse" : "row",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {steps.map((_, index) => (
            <Box
              key={index}
              role="button"
              tabIndex={0}
              onClick={() => setCurrentStep(index)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setCurrentStep(index);
                }
              }}
              sx={{
                width: index === currentStep ? 9 : 7,
                height: index === currentStep ? 9 : 7,
                borderRadius: "50%",
                bgcolor: index === currentStep ? p.hot : "rgba(255, 255, 255, 0.28)",
                cursor: "pointer",
                transition: "all 0.25s ease",
                boxShadow: index === currentStep ? `0 0 12px color-mix(in oklab, ${p.hot} 45%, transparent)` : "none",
                transform: index === currentStep ? "scale(1.12)" : "scale(1)",
              }}
            />
          ))}
        </Box>

        <Box sx={{ width: { xs: 52, sm: 58 }, display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            type="button"
            onClick={handleNext}
            aria-label={currentStep >= steps.length - 1 ? t("common.complete") : t("common.next")}
            sx={{
              width: { xs: 52, sm: 58 },
              height: { xs: 52, sm: 58 },
              borderRadius: "50%",
              background: `linear-gradient(145deg, ${p.hot} 0%, color-mix(in oklab, ${p.hot} 68%, ${p.violet}) 100%)`,
              color: "#fff",
              boxShadow: `0 8px 22px color-mix(in oklab, ${p.hot} 42%, transparent)`,
              "&:hover": {
                filter: "brightness(1.06)",
                transform: "scale(1.04)",
              },
              transition: "transform 0.2s ease, filter 0.2s ease",
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: { xs: 26, sm: 30 } }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingScreen;
